// components/servicing_dashboard/sections/CustomerBreakdown.jsx
// WITH PERFORMANCE OPTIMIZED PREFERENCES - Local state + Save button + Collapsible Details
// FIXED: Scroll position preservation

import { useState, useMemo, useCallback, useEffect, useRef, memo } from 'react';
import { usePagePreferences } from '../../../hooks/useUserPreference';

import { 
  ChevronUpIcon, 
  ChevronDownIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ClockIcon,
  ChartBarIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import MetricCard from '../../common/MetricCard';

// KPI Definitions
const KPI_DEFINITIONS = {
  profitability: "Profit margin calculated as (Invoiced Fees - Billable Incurred) / Invoiced Fees × 100. Shows the percentage of revenue retained as profit after covering billable costs. Higher percentages indicate better profitability.",
  totalHours: "Sum of all billable hours worked across all clients during the period. Includes both project and retainer work.",
  totalClients: "Number of unique clients with billable activity during the period. Includes both project-based and retainer clients.",
  quarterlyData: "Hours worked and profitability percentage for this specific quarter."
};

// ============================================================================
// MEMOIZED COMPONENTS - DEFINED OUTSIDE to prevent re-mounting
// ============================================================================

// Memoized client item to prevent unnecessary re-renders
const ClientItem = memo(({ clientName, isVisible, onClick }) => (
  <div
    className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
    onClick={onClick}
  >
    <input
      type="checkbox"
      checked={isVisible}
      readOnly
      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 pointer-events-none"
    />
    <span className="ml-3 text-sm text-gray-900 flex-1">{clientName}</span>
    {isVisible ? (
      <EyeIcon className="h-4 w-4 text-green-500" />
    ) : (
      <EyeSlashIcon className="h-4 w-4 text-gray-400" />
    )}
  </div>
));

// Visibility Settings Modal - DEFINED OUTSIDE to prevent re-mounting and scroll jumping
const VisibilitySettings = memo(({
  allClientNames,
  localHiddenClients,
  savedHiddenClients,
  hasUnsavedChanges,
  isSaving,
  onClose,
  onToggleClient,
  onSelectAll,
  onSave,
  onCancel
}) => {
  const scrollContainerRef = useRef(null);

  const allVisible = localHiddenClients.size === 0;
  const someHidden = localHiddenClients.size > 0 && localHiddenClients.size < allClientNames.length;

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
      {/* Save/Cancel at top */}
      {hasUnsavedChanges && (
        <div className="p-3 border-b border-gray-200 bg-blue-50">
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={isSaving}
              className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="flex-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Close button in top right */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {allClientNames.length - localHiddenClients.size}/{allClientNames.length} visible
          </span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>
      </div>
      
      {/* Select All Checkbox at top of list */}
      <div className="px-3 pt-2 pb-1">
        <label className="flex items-center px-3 py-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors">
          <input
            type="checkbox"
            checked={allVisible}
            ref={(el) => {
              if (el) el.indeterminate = someHidden;
            }}
            onChange={onSelectAll}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-3 text-sm font-medium text-gray-900">
            {allVisible ? 'Unselect All' : 'Select All'}
          </span>
        </label>
      </div>
      
      {/* Client list - NO scroll preservation needed because component doesn't re-mount */}
      <div ref={scrollContainerRef} className="max-h-96 overflow-y-auto px-3 pb-3">
        {allClientNames.map((clientName) => {
          const isVisible = !localHiddenClients.has(clientName);
          return (
            <ClientItem
              key={clientName}
              clientName={clientName}
              isVisible={isVisible}
              onClick={() => onToggleClient(clientName)}
            />
          );
        })}
      </div>
      
      {/* Info footer */}
      {!hasUnsavedChanges && (
        <div className="px-3 py-2 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-600">
            {savedHiddenClients.length > 0 
              ? `${savedHiddenClients.length} client${savedHiddenClients.length > 1 ? 's' : ''} hidden from table`
              : 'All clients visible in table'}
          </p>
        </div>
      )}
    </div>
  );
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CustomerBreakdown = ({ 
  customerFeeData = [],
  overserviceData = {},
  quarterlyBreakdown = [],
  quarters = [],
  hasQuarterlyData = false,
  dateRangePreset = null,
  loading = false,
  error = null
}) => {
  // UI STATE - doesn't need to persist
  const [sortConfig, setSortConfig] = useState({ key: 'profitability', direction: 'desc' });
  const [showVisibilitySettings, setShowVisibilitySettings] = useState(false);
  const [showClientDetails, setShowClientDetails] = useState(false);
  
  // LOCAL STATE - for fast UI updates before saving
  const [localHiddenClients, setLocalHiddenClients] = useState(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // PERSISTED STATE - from backend (user-specific, NOT year-specific)
  const [preferences, setPreference, setMultiplePreferences, prefsLoading, prefsError, prefsInitialized] = usePagePreferences(
    'customer_breakdown',
    {
      hidden_clients: { hidden_clients: [] }
    }
  );

  // Extract saved preferences (user-specific across all years)
  const savedHiddenClients = preferences.hidden_clients?.hidden_clients || [];
  
  // Regular local state (NOT persisted):
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('by-client');

  // Initialize local state from saved preferences once loaded
  const initializedRef = useRef(false);
  
  useEffect(() => {
    if (prefsInitialized && !hasUnsavedChanges && !initializedRef.current) {
      setLocalHiddenClients(new Set(savedHiddenClients));
      initializedRef.current = true;
    }
  }, [prefsInitialized, hasUnsavedChanges, savedHiddenClients]);

  // Use saved state for display
  const hiddenClients = new Set(savedHiddenClients);

  // Format functions
  const formatHours = (hours) => (hours || 0).toFixed(1);
  const formatPercentage = (value) => `${(value || 0).toFixed(1)}%`;

  const calculateProfitability = useCallback((invoiced, incurred) => {
    if (!invoiced) return 0;
    return (invoiced - incurred)*100 / invoiced;
  }, []);

  const getQuarterlyDataForClient = useCallback((parentCustomer) => {
    if (!hasQuarterlyData || !quarterlyBreakdown.length) return null;
    
    const clientQuarterlyData = quarterlyBreakdown.filter(
      (q) => q.parent_customer === parentCustomer
    );
    
    if (!clientQuarterlyData.length) return null;
    
    const quarterlyAgg = {};
    
    clientQuarterlyData.forEach((item) => {
      if (!quarterlyAgg[item.quarter]) {
        quarterlyAgg[item.quarter] = {
          quarter: item.quarter,
          client_hours: 0,
          incurred_fees: 0,
          invoiced_fees: 0,
        };
      }
      
      quarterlyAgg[item.quarter].client_hours += item.client_hours || 0;
      quarterlyAgg[item.quarter].incurred_fees += item.incurred_fees || 0;
      quarterlyAgg[item.quarter].invoiced_fees += item.invoiced_fees || 0;
    });
    
    const result = {};
    Object.values(quarterlyAgg).forEach((q) => {
      result[q.quarter] = {
        hours: q.client_hours,
        profitability: calculateProfitability(q.invoiced_fees, q.incurred_fees),
      };
    });
    
    return result;
  }, [hasQuarterlyData, quarterlyBreakdown, calculateProfitability]);

  // Get all unique client names
  const allClientNames = useMemo(() => {
    if (!Array.isArray(customerFeeData)) return [];
    
    const validCustomers = customerFeeData.filter(c => {
      const clientType = (c.client_type || '').toLowerCase();
      return clientType === 'retainer' || clientType === 'project';
    });
    
    const clientSet = new Set(validCustomers.map(c => c.parent_customer));
    return Array.from(clientSet).sort();
  }, [customerFeeData]);

  // Toggle client visibility (LOCAL ONLY - fast)
  const toggleClientVisibility = useCallback((clientName) => {
    setLocalHiddenClients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientName)) {
        newSet.delete(clientName);
      } else {
        newSet.add(clientName);
      }
      return newSet;
    });
    setHasUnsavedChanges(true);
  }, []);

  // Show all clients (LOCAL)
  const showAllClients = useCallback(() => {
    setLocalHiddenClients(new Set());
    setHasUnsavedChanges(true);
  }, []);

  // Hide all clients (LOCAL)
  const hideAllClients = useCallback(() => {
    setLocalHiddenClients(new Set(allClientNames));
    setHasUnsavedChanges(true);
  }, [allClientNames]);

  // Handle select all toggle
  const handleSelectAll = useCallback(() => {
    if (localHiddenClients.size === 0) {
      hideAllClients();
    } else {
      showAllClients();
    }
  }, [localHiddenClients.size, hideAllClients, showAllClients]);

  // Save to backend (user-specific, persists across year changes)
  const saveHiddenClients = useCallback(async () => {
    setIsSaving(true);
    try {
      await setPreference('hidden_clients', { 
        hidden_clients: Array.from(localHiddenClients) 
      });
      setHasUnsavedChanges(false);
      setShowVisibilitySettings(false);
    } catch (err) {
      console.error('Error saving hidden clients:', err);
    } finally {
      setIsSaving(false);
    }
  }, [localHiddenClients, setPreference]);

  // Cancel changes
  const cancelChanges = useCallback(() => {
    setLocalHiddenClients(new Set(savedHiddenClients));
    setHasUnsavedChanges(false);
    setShowVisibilitySettings(false);
  }, [savedHiddenClients]);

  // Calculate summary metrics (excluding hidden clients)
  const summaryMetrics = useMemo(() => {
    if (!Array.isArray(customerFeeData)) return {};
    
    const validCustomers = customerFeeData.filter(c => {
      const clientType = (c.client_type || '').toLowerCase();
      const isValidType = clientType === 'retainer' || clientType === 'project';
      return isValidType;
    });
    
    const projects = validCustomers.filter(c => (c.client_type || '').toLowerCase() === 'project');
    const retainers = validCustomers.filter(c => (c.client_type || '').toLowerCase() === 'retainer');
    
    const totalHours = validCustomers.reduce((sum, c) => sum + (c.client_hours || 0), 0);
    const totalIncurredFees = validCustomers.reduce((sum, c) => sum + (c.incurred_fees || 0), 0);
    const totalInvoicedFees = validCustomers.reduce((sum, c) => sum + (c.invoiced_fees || 0), 0);
    
    const overallProfitability = calculateProfitability(totalInvoicedFees, totalIncurredFees);
    
    const uniqueClients = new Set(validCustomers.map(c => c.parent_customer));
    
    return {
      totalCustomers: validCustomers.length,
      uniqueClientCount: uniqueClients.size,
      projectCount: projects.length,
      retainerCount: retainers.length,
      totalHours,
      overallProfitability
    };
  }, [customerFeeData, calculateProfitability]);

  // Filter by type (excluding hidden clients)
  const filteredByType = useMemo(() => {
    if (!Array.isArray(customerFeeData) || customerFeeData.length === 0) return [];
    
    const validCustomers = customerFeeData.filter(c => {
      const clientType = (c.client_type || '').toLowerCase();
      const isValidType = clientType === 'retainer' || clientType === 'project';
      const isVisible = !hiddenClients.has(c.parent_customer);
      return isValidType && isVisible;
    });
    
    switch (filterType) {
      case 'project':
        return validCustomers.filter(c => (c.client_type || '').toLowerCase() === 'project');
      case 'retainer':
        return validCustomers.filter(c => (c.client_type || '').toLowerCase() === 'retainer');
      default:
        return validCustomers;
    }
  }, [customerFeeData, filterType, hiddenClients]);

  // Group data based on view mode
  const viewData = useMemo(() => {
    if (viewMode === 'by-client') {
      const grouped = {};
      
      filteredByType.forEach(item => {
        const clientName = item.parent_customer || 'N/A';
        
        if (!grouped[clientName]) {
          grouped[clientName] = {
            parent_customer: clientName,
            customer: clientName,
            client_hours: 0,
            incurred_fees: 0,
            invoiced_fees: 0,
            employee_count: item.employee_count || 0,
            project_count: 0,
            retainer_count: 0,
            items: []
          };
        }
        
        grouped[clientName].client_hours += item.client_hours || 0;
        grouped[clientName].incurred_fees += item.incurred_fees || 0;
        grouped[clientName].invoiced_fees += item.invoiced_fees || 0;
        grouped[clientName].items.push(item);
        
        if ((item.client_type || '').toLowerCase() === 'project') {
          grouped[clientName].project_count++;
        } else if ((item.client_type || '').toLowerCase() === 'retainer') {
          grouped[clientName].retainer_count++;
        }
      });
      
      return Object.values(grouped).map(client => ({
        ...client,
        profitability: calculateProfitability(client.invoiced_fees, client.incurred_fees),
        quarterly: hasQuarterlyData ? getQuarterlyDataForClient(client.parent_customer) : null
      }));
    } else {
      const projects = filteredByType
        .filter(c => (c.client_type || '').toLowerCase() === 'project')
        .map(item => ({
          ...item,
          profitability: calculateProfitability(item.invoiced_fees || 0, item.incurred_fees || 0),
          quarterly: hasQuarterlyData ? getQuarterlyDataForClient(item.parent_customer) : null
        }));
      
      const retainers = filteredByType
        .filter(c => (c.client_type || '').toLowerCase() === 'retainer')
        .map(item => ({
          ...item,
          profitability: calculateProfitability(item.invoiced_fees || 0, item.incurred_fees || 0),
          quarterly: hasQuarterlyData ? getQuarterlyDataForClient(item.parent_customer) : null
        }));
      
      return { projects, retainers };
    }
  }, [filteredByType, viewMode, hasQuarterlyData, calculateProfitability, getQuarterlyDataForClient]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key] || 0;
      const bValue = b[sortConfig.key] || 0;
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  };

  const getProfitabilityColor = (profitability) => {
    if (profitability >= 30) return 'text-green-600 bg-green-50';
    if (profitability >= 15) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const InfoTooltip = ({ text }) => (
    <div className="group relative inline-flex ml-1">
      <InformationCircleIcon className="h-3.5 w-3.5 text-white hover:text-blue-200 cursor-help transition-colors" />
      <div className="absolute left-1/2 -translate-x-1/2 bottom-auto top-full mt-2 w-64 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50 shadow-xl">
        <div className="leading-relaxed">{text}</div>
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-0">
          <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-gray-900"></div>
        </div>
      </div>
    </div>
  );

  const formatQuarterLabel = (quarter) => {
    const match = quarter.match(/Q\d/);
    return match ? match[0] : quarter;
  };

  const typeFilterOptions = [
    { value: 'all', label: `All (${summaryMetrics.totalCustomers})` },
    { value: 'project', label: `Projects (${summaryMetrics.projectCount})` },
    { value: 'retainer', label: `Retainers (${summaryMetrics.retainerCount})` }
  ];

  const viewModeOptions = [
    { value: 'by-client', label: 'By Client' },
    { value: 'by-project-retainer', label: 'By Project or Retainer' }
  ];

  const renderTable = (data, title = null) => {
    const sortedData = sortData(data);
    
    const columns = [
      { key: 'name', label: title === 'Projects' ? 'Project / Client' : title === 'Retainers' ? 'Customer' : 'Client Name', align: 'left', tooltip: null, sticky: true },
      { key: 'client_hours', label: 'Total Hours', align: 'center', tooltip: KPI_DEFINITIONS.totalHours },
      { key: 'profitability', label: 'Overall Profit %', align: 'center', tooltip: KPI_DEFINITIONS.profitability }
    ];

    const quarterlyColumns = [];
    if (hasQuarterlyData && quarters.length > 0) {
      quarters.forEach(quarter => {
        quarterlyColumns.push({
          key: quarter,
          label: formatQuarterLabel(quarter),
          align: 'center',
          tooltip: KPI_DEFINITIONS.quarterlyData,
          isQuarterly: true
        });
      });
    }

    const finalColumns = [
      { key: 'employee_count', label: 'Employees', align: 'center', tooltip: null }
    ];

    const allColumns = [...columns, ...quarterlyColumns, ...finalColumns];

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {title && (
          <div className="bg-gradient-to-r from-brightblue-600 to-brightblue-500 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-white">{title}</h3>
              {hasQuarterlyData && quarters.length > 0 && (
                <div className="group relative inline-flex">
                  <InformationCircleIcon className="h-4 w-4 text-white hover:text-blue-200 cursor-help transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-auto top-full mt-2 w-72 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50 shadow-xl">
                    <div className="leading-relaxed">
                      <span className="font-semibold">Quarterly breakdown active:</span> Each quarter column shows profitability % (top) and hours (bottom). Scroll horizontally to view all quarters.
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-0">
                      <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-gray-900"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setShowVisibilitySettings(!showVisibilitySettings)}
                className="p-2 text-white hover:bg-brightblue-700 rounded-lg transition-colors relative"
                title="Client Visibility Settings"
              >
                <Cog6ToothIcon className="h-5 w-5" />
                {savedHiddenClients.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
                    {savedHiddenClients.length}
                  </span>
                )}
              </button>
              {showVisibilitySettings && (
                <VisibilitySettings
                  allClientNames={allClientNames}
                  localHiddenClients={localHiddenClients}
                  savedHiddenClients={savedHiddenClients}
                  hasUnsavedChanges={hasUnsavedChanges}
                  isSaving={isSaving}
                  onClose={() => setShowVisibilitySettings(false)}
                  onToggleClient={toggleClientVisibility}
                  onSelectAll={handleSelectAll}
                  onSave={saveHiddenClients}
                  onCancel={cancelChanges}
                />
              )}
            </div>
          </div>
        )}
        
        {!title && (
          <div className="bg-gradient-to-r from-brightblue-600 to-brightblue-500 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-white">Client Details</h3>
              {hasQuarterlyData && quarters.length > 0 && (
                <div className="group relative inline-flex">
                  <InformationCircleIcon className="h-4 w-4 text-white hover:text-blue-200 cursor-help transition-colors" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-auto top-full mt-2 w-72 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50 shadow-xl">
                    <div className="leading-relaxed">
                      <span className="font-semibold">Quarterly breakdown active:</span> Each quarter column shows profitability % (top) and hours (bottom). Scroll horizontally to view all quarters.
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-0">
                      <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-gray-900"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setShowVisibilitySettings(!showVisibilitySettings)}
                className="p-2 text-white hover:bg-brightblue-700 rounded-lg transition-colors relative"
                title="Client Visibility Settings"
              >
                <Cog6ToothIcon className="h-5 w-5" />
                {hiddenClients.size > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
                    {hiddenClients.size}
                  </span>
                )}
              </button>
              {showVisibilitySettings && (
                <VisibilitySettings
                  allClientNames={allClientNames}
                  localHiddenClients={localHiddenClients}
                  savedHiddenClients={savedHiddenClients}
                  hasUnsavedChanges={hasUnsavedChanges}
                  isSaving={isSaving}
                  onClose={() => setShowVisibilitySettings(false)}
                  onToggleClient={toggleClientVisibility}
                  onSelectAll={handleSelectAll}
                  onSave={saveHiddenClients}
                  onCancel={cancelChanges}
                />
              )}
            </div>
          </div>
        )}
        
        <div className="overflow-x-auto relative">
          {hasQuarterlyData && quarters.length > 0 && (
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-200 to-transparent pointer-events-none z-10" />
          )}
          
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-brightblue-500">
              <tr>
                {allColumns.map(({ key, label, align, tooltip, isQuarterly, sticky }) => (
                  <th 
                    key={key}
                    className={`
                      px-3 py-3 text-${align} text-xs font-medium text-white uppercase tracking-wider
                      ${!isQuarterly ? 'cursor-pointer hover:bg-brightblue-600' : ''} 
                      ${isQuarterly ? 'bg-brightblue-600 w-20' : ''}
                      ${sticky ? 'sticky left-0 z-20 bg-brightblue-500 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] min-w-[200px]' : key === 'client_hours' || key === 'profitability' ? 'w-28' : ''}
                      transition-colors
                    `}
                    onClick={() => !isQuarterly && handleSort(key)}
                  >
                    <div className={`flex items-center ${align === 'center' ? 'justify-center' : ''} space-x-1`}>
                      <span className={isQuarterly ? 'text-xs' : ''}>{label}</span>
                      {!isQuarterly && sortConfig.key === key && (
                        sortConfig.direction === 'asc' ? 
                          <ChevronUpIcon className="w-4 h-4" /> : 
                          <ChevronDownIcon className="w-4 h-4" />
                      )}
                      {tooltip && <InfoTooltip text={tooltip} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedData.map((customer, index) => {
                const profitability = customer.profitability || 0;
                const quarterlyData = customer.quarterly || {};

                return (
                  <tr 
                    key={`${customer.customer}-${index}`} 
                    className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
                  >
                    <td className={`px-3 py-3 sticky left-0 z-10 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] min-w-[200px]`}>
                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-8 w-8 mt-0.5">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-3 min-w-0 flex-1">
                          {title === 'Projects' ? (
                            <div className="space-y-0.5">
                              <div className="text-sm font-semibold text-gray-900 break-words">
                                {customer.customer === 'Unassigned' ? 'Unassigned Work' : customer.customer}
                              </div>
                              <div className="text-xs text-gray-500 break-words">
                                {customer.parent_customer || 'N/A'}
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm font-medium text-gray-900 break-words">
                              {customer.parent_customer}
                              {viewMode === 'by-client' && (customer.project_count > 0 || customer.retainer_count > 0) && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {customer.project_count > 0 && `Project:${customer.project_count}`}
                                  {customer.project_count > 0 && customer.retainer_count > 0 && ', '}
                                  {customer.retainer_count > 0 && `Retainer:${customer.retainer_count}`}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap text-center text-gray-900 w-28">
                      <div className="text-sm font-medium">{formatHours(customer.client_hours)}h</div>
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap text-center w-28">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProfitabilityColor(profitability)}`}>
                        {formatPercentage(profitability)}
                      </span>
                    </td>

                    {hasQuarterlyData && quarters.map(quarter => (
                      <td key={quarter} className="px-2 py-2 whitespace-nowrap text-center bg-blue-50 w-20">
                        {quarterlyData[quarter] ? (
                          <div className="flex flex-col items-center space-y-0.5">
                            <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getProfitabilityColor(quarterlyData[quarter].profitability)}`}>
                              {formatPercentage(quarterlyData[quarter].profitability)}
                            </span>
                            <div className="text-xs font-medium text-gray-700">
                              {formatHours(quarterlyData[quarter].hours)}h
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    ))}
                    
                    <td className="px-3 py-3 whitespace-nowrap text-center text-gray-900">
                      <div className="text-sm font-medium">{customer.employee_count}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sortedData.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
            <p className="text-lg font-medium">No data available</p>
          </div>
        )}
      </div>
    );
  };

  if (loading || prefsLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error || prefsError) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>Error: {error || prefsError}</p>
      </div>
    );
  }

  if (!Array.isArray(customerFeeData) || customerFeeData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2">No customer fee data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        {/* Summary Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            title="Total Clients"
            tooltip={KPI_DEFINITIONS.totalClients}
            value={summaryMetrics.uniqueClientCount}
            changeLabel={`${summaryMetrics.retainerCount} Retainers • ${summaryMetrics.projectCount} Projects`}
            icon={UsersIcon}
            loading={loading}
            isClickable={false}
          />
          
          <MetricCard
            title="Total Hours"
            tooltip={KPI_DEFINITIONS.totalHours}
            value={`${formatHours(summaryMetrics.totalHours)}h`}
            changeLabel="Across all clients"
            icon={ClockIcon}
            loading={loading}
            isClickable={false}
          />
          
          <MetricCard
            title="Overall Profitability"
            tooltip={KPI_DEFINITIONS.profitability}
            value={formatPercentage(summaryMetrics.overallProfitability)}
            changeLabel="Average profitability"
            icon={ChartBarIcon}
            loading={loading}
            isClickable={false}
          />
        </div>

        {/* Arrow toggle indicator */}
        <div 
          className="mt-6 pt-4 border-t border-gray-100 cursor-pointer"
          onClick={() => setShowClientDetails(!showClientDetails)}
        >
          <div className="text-xs text-gray-600 flex items-center justify-end gap-2 hover:text-gray-900 transition-colors">
            <span>Click to {showClientDetails ? 'hide' : 'view'} client details</span>
            <div className="group flex items-center justify-center h-6 w-6 rounded-full border-2 border-gray-400 hover:border-primary-500 hover:bg-primary-500 hover:scale-110 transition-all">
              {showClientDetails ? (
                <ArrowUpIcon className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
              ) : (
                <ArrowDownIcon className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Client Details Section - Collapsible */}
      {showClientDetails && (
        <>
          {/* Filter Controls */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">View:</label>
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {viewModeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Type:</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {typeFilterOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ml-auto text-sm text-gray-600">
                Showing <span className="font-semibold">
                  {viewMode === 'by-client' 
                    ? Array.isArray(viewData) ? viewData.length : 0
                    : (viewData.projects?.length || 0) + (viewData.retainers?.length || 0)
                  }
                </span> {viewMode === 'by-client' ? 'clients' : 'items'}
                {hiddenClients.size > 0 && (
                  <span className="text-gray-500"> ({hiddenClients.size} hidden)</span>
                )}
              </div>
            </div>
          </div>

          {/* Tables */}
          {viewMode === 'by-client' ? (
            renderTable(viewData)
          ) : (
            <div className="space-y-6">
              {filterType !== 'project' && viewData.retainers?.length > 0 && (
                renderTable(viewData.retainers, 'Retainers')
              )}
              {filterType !== 'retainer' && viewData.projects?.length > 0 && (
                renderTable(viewData.projects, 'Projects')
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomerBreakdown;